import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Image, ViewStyle, ImageStyle} from 'react-native';

import {ResetPassword} from 'components/common/reset-password/ResetPassword';
import {spacing} from 'theme/Units';
import colors from 'theme/Colors';

import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';

import {ForgetPasswordModal} from 'components/auth-screen/ForgetPasswordModal';
import {useDispatch, useSelector} from 'react-redux';
import {
  resetPasswordRequest,
  resetMessage,
  resetPasswordOtpRequest,
  setNewPasswordRequest,
  verifyOTPRequest,
  resetAuth,
} from 'redux/slices/authSlice';
import {
  getAuthSuccess,
  getAuthError,
  getAuthData,
} from 'redux/selectors/authSelector';
import {useToast} from 'hooks';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {useResponsiveStyles} from 'hooks/useResponsiveStyles';

type propType = Readonly<{
  mode: 'forgot' | 'reset';
}> &
  NativeStackScreenProps<RootNavigatorParams>;

export function ResetPasswordScreen({navigation, mode = 'reset'}: propType) {
  const dispatch = useDispatch();
  const resetPasswordSuccess = useSelector(getAuthSuccess);
  const authData = useSelector(getAuthData);
  const resetPasswordFailure = useSelector(getAuthError);
  const {showToasts} = useToast();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const authEmail = authData?.email || '';

  type initialValuesType = {
    current_password: string;
    new_password: string;
    confirm_password: string;
  };
  const initialValues: initialValuesType = {
    current_password: '',
    new_password: '',
    confirm_password: '',
  };

  const gotoSetNewPasswordScreen = useCallback(() => {
    navigation.replace(Routes.FORGOT_PASSWORD);
  }, [navigation]);

  const gotoProfileScreen = useCallback(() => {
    navigation.navigate(Routes.VIEW_PROFILE);
  }, [navigation]);

  //useeffect for success message
  useEffect(() => {
    if (resetPasswordSuccess) {
      if (['S-10072', 'S-10073'].includes(resetPasswordSuccess)) {
        // set otp sent to true
        if (resetPasswordSuccess === 'S-10073') {
          setIsOTPSent(true);
        }
        // when otp is verified, redirect user to forgot password screen.
        // it is different from reset password as it does not have current password input field
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(resetPasswordSuccess as SuccessCodes),
          'success',
        );
        if (resetPasswordSuccess === 'S-10072') {
          gotoProfileScreen();
        }
      }
      if (resetPasswordSuccess === 'S-10074') {
        gotoSetNewPasswordScreen();
      }
      dispatch(resetMessage());
    }
  }, [
    dispatch,
    resetPasswordSuccess,
    showToasts,
    gotoSetNewPasswordScreen,
    gotoProfileScreen,
  ]);

  //useeffect for error message
  useEffect(() => {
    if (resetPasswordFailure) {
      if (resetPasswordFailure === 'E-10087') {
        setModalOpen(false);
      }
      if (resetPasswordFailure === 'E-10086') {
        setModalOpen(false);
        showToasts('warning', getErrorMessage(resetPasswordFailure), 'warning');
        dispatch(resetAuth());
      } else {
        showToasts('error', getErrorMessage(resetPasswordFailure as any), 'error');
      }
      dispatch(resetMessage());
    }
  }, [dispatch, resetPasswordFailure, showToasts]);

  function handleResetPassword({
    confirm_password,
    ...values
  }: initialValuesType) {
    if (mode === 'reset') {
      dispatch(resetPasswordRequest(values));
    } else {
      dispatch(
        setNewPasswordRequest({
          email: authEmail,
          new_password: values.new_password,
        }),
      );
    }
  }

  function handleForgetPassword() {
    dispatch(resetPasswordOtpRequest({email: authEmail}));
  }

  function handleOTPSubmit(otp: string) {
    dispatch(verifyOTPRequest({email: authEmail, otp}));
  }

  function handleCancel() {
    navigation.navigate(Routes.VIEW_PROFILE);
  }

  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);

  return (
    <View style={styles.container}>
      <View>
        <Image
          style={styles.image}
          source={require('assets/illustrations/reset-password.png')}
        />
      </View>
      <View>
        <ResetPassword
          onCancel={handleCancel}
          mode={mode}
          header="Reset your Password"
          setModalOpen={setModalOpen}
          onSubmit={handleResetPassword}
          initialValues={initialValues}
        />
        {mode === 'reset' ? (
          <ForgetPasswordModal
            otpSent={isOTPSent}
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onSubmit={handleForgetPassword}
            onOTPSubmit={handleOTPSubmit}
          />
        ) : null}
      </View>
    </View>
  );
}

type styleTypes = {
  container: ViewStyle;
  image: ImageStyle;
};

const baseStyles: styleTypes = StyleSheet.create({
  container: {
    flex: 1,
    margin: spacing['6'],
    paddingVertical: spacing['4'],
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    flexDirection: 'row',
    gap: spacing['12'],
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: 350,
    height: 350,
  },
});

const responsive: Record<number, Partial<styleTypes>> = {
  640: StyleSheet.create({
    container: {
      flexDirection: 'column',
      gap: spacing['6'],
    },
    image: {
      width: 250,
      height: 250,
    },
  }),
  480: StyleSheet.create({
    container: {
      flexDirection: 'column',
      gap: spacing['6'],
    },
    image: {
      width: 200,
      height: 200,
    },
  }),
};
